import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { authService } from 'services/auth/authService';

import { useChangePasswordAlerts } from './useChangePasswordAlerts';

interface FormData {
  currentPassword: string;
  newPassword: string;
  newPasswordConfirmation: string;
}

export const useChangePasswordForm = () => {
  const { t } = useTranslation('myProfile');
  const { passwordChanged, invalidOldPassword, invalidRequest } = useChangePasswordAlerts();

  const initialValues: FormData = {
    currentPassword: '',
    newPassword: '',
    newPasswordConfirmation: '',
  };

  const validationSchema = Yup.object().shape({
    currentPassword: Yup.string().ensure().required(t('password.verification.password-required')),
    newPassword: Yup.string()
      .ensure()
      .required(t('password.verification.password-required'))
      .min(8, t('password.verification.password-too-short'))
      .test('test ascii', t('password.verification.password-invalid-chars'), pass => {
        return ![...pass].some((char: string) => char.charCodeAt(0) > 127);
      })
      .test('test trailing spaces', t('password.verification.password-trailing-space'), pass => {
        return !pass.startsWith(' ') && !pass.endsWith(' ');
      }),
    newPasswordConfirmation: Yup.string()
      .required(t('password.verification.password-required'))
      .oneOf([Yup.ref('newPassword')], t('password.verification.password-no-match')),
  });

  const onSubmit = async (data: FormData, { resetForm }: { resetForm: () => void }) => {
    const currentPassword = data.currentPassword.trim();
    const newPassword = data.newPasswordConfirmation.trim();

    try {
      await authService.changePassword(currentPassword, newPassword).then(() => {
        passwordChanged();
        resetForm();
      });
    } catch (error) {
      handleError(error);
    }
  };

  const handleError = (error: any) => {
    if (error.response?.status === 400) {
      invalidOldPassword();
    } else {
      invalidRequest();
    }
  };

  return useFormik({ initialValues, validationSchema, onSubmit });
};
